import { AfterViewInit, Inject, Injectable } from '@angular/core';
import { catchError, first, retryWhen, switchMap, timeout } from 'rxjs/operators'
import { LbHttpService } from '../lb-http.service'
import { LocalStorageService } from '../utils/local-storage.service'
import moment from 'moment-timezone'
import { forkJoin, Observable, of, throwError } from 'rxjs'
import { ConfigService } from '../config.service'
import { fromPromise } from 'rxjs/internal-compatibility'
import { HttpClient } from '@angular/common/http'
import { ErrorService } from '../utils/error.service'
import { ConfirmDialogComponent } from '../../dialog/confirm-dialog/confirm-dialog.component'
import { MatDialog } from '@angular/material/dialog'
import { L10N_LOCALE, L10nLocale } from 'angular-l10n'
import { LbTranslationService } from '@affluences/lb-utils-front'

@Injectable( {
    providedIn: 'root'
} )
export class TimeslotsService{

    public timezone = ''

    constructor (
        @Inject( L10N_LOCALE ) public locale: L10nLocale,
        public lbHttpService: LbHttpService,
        public lbTranslationService: LbTranslationService,
        private http: HttpClient,
        public localStorageService: LocalStorageService,
        public config: ConfigService,
        public dialog: MatDialog,
        public errorService: ErrorService
    ) {
    }

    public getStartOfDay () {
        return moment.tz( Date.now(), this.getTimezone() ).startOf( 'day' )
    }

    public getEndOfDay () {
        return moment.tz( Date.now(), this.getTimezone() ).endOf( 'day' )
    }

    public getFullDate ( date, timezone? ) {
        let text = ''
        const lang = this.locale.language.slice(0, 2)
        const dayFormat = lang === 'fr' ? 'DD/MM/YYYY' : 'MM/DD/YYYY'
        if ( date ) {
            if ( !timezone ) {
                timezone = this.getTimezone()
            }
            if ( typeof (date) === 'string' ) {
                date = moment.tz( date, moment.ISO_8601, timezone )
            } else if ( typeof (date) === 'number' ) {
                date = moment.tz( date, timezone )
            }

            const currentDay = moment.tz( Date.now(), timezone ).format( dayFormat )
            const nextDay = moment.tz( Date.now(), timezone ).add( 1, 'day' ).format( dayFormat )
            const dateDay = moment.tz( date.valueOf(), timezone ).format( dayFormat )


            if ( dateDay === currentDay ) {
                text += this.lbTranslationService.translateFromKey( 'endpoint_timeslotService_globalTime_text_today', { lang: this.locale.language } )
                text += moment.tz( date.valueOf(), timezone ).format( 'HH:mm' )
            } else if ( dateDay === nextDay ) {
                text += this.lbTranslationService.translateFromKey( 'endpoint_timeslotService_globalTime_text_tomorrow', { lang: this.locale.language } )
                text += moment.tz( date.valueOf(), timezone ).format( 'HH:mm' )
            } else {
                text += this.lbTranslationService.translateFromKey( 'endpoint_timeslotService_globalTime_text_the', { lang: this.locale.language } )
                text += moment.tz( date.valueOf(), timezone ).format( dayFormat )
                text += this.lbTranslationService.translateFromKey( 'endpoint_timeslotService_globalTime_text_at', { lang: this.locale.language } )
                text += moment.tz( date.valueOf(), timezone ).format( 'HH:mm' )
            }
        } else {
            text = '-'
        }
        return text
    }

    public getTimeWithTimezone ( time, timezone? ) {
        if ( !timezone ) {
            timezone = this.getTimezone()
        }
        if ( typeof (time) === 'string' ) {
            return moment.tz( time, moment.ISO_8601, timezone ).format( 'HH[h]mm' )
        } else if ( typeof (time) === 'number' ) {
           return moment.tz( time, timezone ).format( 'HH[h]mm' )
        }
        return '-'
    }

    public getDiffTime ( timestamp: number ): string {
        let res: string = ''
        if ( timestamp ) {
            let time = timestamp
            time = time < 0 ? time * -1 : time
            const nbMin = Math.round( time / 60000 )
            if ( nbMin < 60 ) {
                return nbMin + ' min'
            } else {
                return Math.floor( nbMin / 60 ) + 'h ' + (nbMin % 60) + ' min'
            }
        } else {
            res = '-'
        }
        return res
    }

    public getTicketTime ( field: string, ticket: any ): string {
        let res: string = ''
        if ( ticket !== null && 'timeline' in ticket && field in ticket['timeline'] && ticket['timeline'][field] ) {
            res += moment.tz( ticket['timeline'][field], moment.ISO_8601, this.getTimezone() ).format( 'HH[h]mm' )
        } else {
            res = '-'
        }
        return res
    }

    getTicketDiffTime ( field: string, ticket: any ): string {
        let res: string = ''
        if ( ticket !== null && 'timeline' in ticket && field in ticket['timeline'] && ticket['timeline'][field] ) {
            let time = Date.now() - moment.tz( ticket['timeline'][field], moment.ISO_8601, this.timezone ).valueOf()
            time = time < 0 ? time * -1 : time
            const nbMin = Math.round( time / 60000 )
            if ( nbMin < 60 ) {
                return nbMin + ' min'
            } else {
                return Math.floor( nbMin / 60 ) + 'h ' + (nbMin % 60) + ' min'
            }
        } else {
            res = '-'
        }
        return res
    }

    public getTimezone ( queueId?: string ) {
        if ( queueId ) {
            // TODO: load queue timezone
            return this.config.defaultTimezone
        } else {
            return this.localStorageService.getSelectedQueue() && this.localStorageService.getSelectedQueue().timezone
                ? this.localStorageService.getSelectedQueue().timezone
                : this.config.defaultTimezone
        }
    }

    public getEndpointTimeslotsOfTheDay ( saveInLocalStorage = false ) {
        const endpointId = this.localStorageService.getSelectedEndpoint() && this.localStorageService.getSelectedEndpoint().endpointId
            ? this.localStorageService.getSelectedEndpoint().endpointId
            : null

        if ( endpointId ) {
            const startAt = this.getStartOfDay().toISOString()
            const endAt = this.getEndOfDay().toISOString()
            return this.httpGetEndpointTimeslots( endpointId, startAt, endAt, saveInLocalStorage )
        } else {
            return throwError( 'ENDPOINT_ID_UNDEFINED' )
        }
    }

    public loadEndpointTimeslots () {
        const endpointId = this.localStorageService.getSelectedEndpoint().endpointId
        const startAt = this.getStartOfDay().toISOString()
        const endAt = this.getEndOfDay().toISOString()

        return this.httpGetPlaceTimeslots( this.localStorageService.getSelectedQueue().placeId ).pipe(
            switchMap( ( placeTimeslots: any ) => {
                this.localStorageService.setCurrentPlaceTimeslots( placeTimeslots )
                return this.httpGetEndpointTimeslots( endpointId, startAt, endAt )
            } ),
            switchMap( ( res: any ) => {
                const timeslotsRes = []
                for ( const time of res ) {
                    const start = moment.tz( time.startAt, moment.ISO_8601, this.getTimezone() ).format( 'HH:mm' )
                    const end = moment.tz( time.endAt, moment.ISO_8601, this.getTimezone() ).format( 'HH:mm' )
                    timeslotsRes.push( { start: start, end: end } )
                }
                return of( timeslotsRes )
            } )
        )
    }

    public saveEndpointTimeslots ( timeslots ): Observable<any> {
        const timeslotsToInsert = []
        for ( const k in timeslots ) {
            timeslotsToInsert.push( {
                startAt: this.getTimeFromHour( timeslots[k].start, true ),
                endAt: this.getTimeFromHour( timeslots[k].end, true )
            } )
        }
        if ( timeslotsToInsert.length > 0 ) {
            return this.httpSetEndpointTimeslots( timeslotsToInsert )
        } else {
            return this.httpDeleteEndpointTimeslots()
        }
    }

    public checkEndpointTimeslot ( timeslots ) {
        const placeTimeslots = this.localStorageService.getCurrentPlaceTimeslots()
        let errorName = ''

        for ( const k in timeslots ) {
            const start1 = this.getTimeFromHour( timeslots[k].start, false )
            const end1 = this.getTimeFromHour( timeslots[k].end, false )
            timeslots[k].startError = false
            timeslots[k].endError = false

            if ( start1 === end1 ) {
                timeslots[k].startError = true
                timeslots[k].endError = true
                if ( errorName === '' ) {
                    errorName = 'VOID_TIMESLOT'
                }
            }

            for ( const j in timeslots ) {
                if ( k !== j ) {
                    const start2 = this.getTimeFromHour( timeslots[j].start, false )
                    const end2 = this.getTimeFromHour( timeslots[j].end, false )

                    if ( end1 >= start2 && end1 <= end2 ) {
                        timeslots[k].endError = true
                        if ( errorName === '' ) {
                            errorName = 'OVERLAP'
                        }
                    }
                    if ( start1 >= start2 && start1 <= end2 ) {
                        timeslots[k].startError = true
                        if ( errorName === '' ) {
                            errorName = 'OVERLAP'
                        }
                    }
                    if ( end1 === end2 ) {
                        timeslots[k].endError = true
                        if ( errorName === '' ) {
                            errorName = 'OVERLAP'
                        }
                    }
                    if ( start1 === start2 ) {
                        timeslots[k].startError = true
                        if ( errorName === '' ) {
                            errorName = 'OVERLAP'
                        }
                    }
                }
            }

            let startInPlaceSlots = false
            let endInPlaceSlots = false
            for ( const r in placeTimeslots ) {
                if (
                    start1 as number >= moment.tz( placeTimeslots[r].startAt, moment.ISO_8601, this.getTimezone() ).valueOf()
                    && start1 as number <= moment.tz( placeTimeslots[r].endAt, moment.ISO_8601, this.getTimezone() ).valueOf()
                ) {
                    startInPlaceSlots = true
                }
                if (
                    end1 as number >= moment.tz( placeTimeslots[r].startAt, moment.ISO_8601, this.getTimezone() ).valueOf() 
                    && end1 as number <= moment.tz( placeTimeslots[r].endAt, moment.ISO_8601, this.getTimezone() ).valueOf()
                ) {
                    endInPlaceSlots = true
                }
            }

            if ( !startInPlaceSlots ) {
                if ( errorName === '' ) {
                    errorName = 'PLACE_TIMESLOTS'
                }
                timeslots[k].startError = true
            }
            if ( !endInPlaceSlots ) {
                if ( errorName === '' ) {
                    errorName = 'PLACE_TIMESLOTS'
                }
                timeslots[k].endError = true
            }
        }

        return errorName
    }

    public getTimeFromHour ( time: string, inDateFormat: boolean ) {
        const hour = parseInt( time.replace( /:[0-9]+$/gi, '' ), 10 )
        const minute = parseInt( time.replace( /^[0-9]+:/gi, '' ), 10 )
        if ( inDateFormat ) {
            return moment.tz( Date.now(), this.getTimezone() ).startOf( 'day' ).add( hour, 'hours' ).add( minute, 'minutes' ).toISOString()
        } else {
            return moment.tz( Date.now(), this.getTimezone() ).startOf( 'day' ).add( hour, 'hours' ).add( minute, 'minutes' ).valueOf()
        }
    }

    public currentlyOutOfPlaceTimeslots () {
        const placeTimeslots = this.localStorageService.getCurrentPlaceTimeslots()
        const now = Date.now()
        for ( const time of placeTimeslots ) {
            const start = moment.tz( time.startAt, moment.ISO_8601, this.getTimezone() ).valueOf()
            const end = moment.tz( time.endAt, moment.ISO_8601, this.getTimezone() ).valueOf()
            if ( now >= start && now <= end ) {
                return false
            }
        }
        return true
    }

    public currentlyOutOfEndpointTimeslots ( endpointTimeslots ) {
        const now = Date.now()
        for ( const time of endpointTimeslots ) {
            const start = moment.tz( time.startAt, moment.ISO_8601, this.getTimezone() ).valueOf()
            const end = moment.tz( time.endAt, moment.ISO_8601, this.getTimezone() ).valueOf()
            if ( now >= start && now <= end ) {
                return false
            }
        }
        return true
    }

    public getPlaceMinMaxTimeslots (): {min: moment.Moment, max: moment.Moment} {
        const placeTimeslots = this.localStorageService.getCurrentPlaceTimeslots()
        let min = null
        let max = null
        for ( const time of placeTimeslots ) {
            const start = moment.tz( time.startAt, moment.ISO_8601, this.getTimezone() )
            const end = moment.tz( time.endAt, moment.ISO_8601, this.getTimezone() )
            if ( min === null || min.valueOf() > start.valueOf() ) {
                min = start
            }
            if ( max === null || max.valueOf() < end.valueOf() ) {
                max = end
            }
        }
        return { min: min, max: max }
    }

    public getCurrentTimeslots ( timeslots ) {
        const now = Date.now()
        for ( const t of timeslots ) {
            const start = moment.tz( t.startAt, moment.ISO_8601, this.timezone ).valueOf()
            const end = moment.tz( t.endAt, moment.ISO_8601, this.timezone ).valueOf()
            if ( now >= start && now <= end ) {
                return t
            }
        }
        return null
    }

    public setTimeslotsToEndNow () {
        return this.getEndpointTimeslotsOfTheDay().pipe(
            switchMap( ( timeslots: any ) => {
                const currentTimeslot = this.getCurrentTimeslots( timeslots )
                if ( currentTimeslot ) {
                    const newTimeslots = {
                        startAt: currentTimeslot.startAt,
                        endAt: moment.tz( (Date.now() - 120000), this.timezone ).toISOString()
                    }
                    return this.httpSetEndpointTimeslots( [newTimeslots] )
                } else {
                    return of( true )
                }
            } )
        )
    }

    public httpOpenBooking ( queueId ) {
        let url = this.config.openBooking
        url = url.replace( '{queueId}', queueId )

        return fromPromise( this.lbHttpService.generateHeaderForLineberty( true, false, 'validation' ) ).pipe(
            timeout( this.config.defaultHttpTimeout ),
            switchMap( ( httpOptions: any ) => {
                return this.http.put( url, null, httpOptions )
            } ),
            first(),
            retryWhen( this.lbHttpService.genericRetryStrategy( {} ) ),
            catchError( ( error ) => {
                this.errorService.logError( 'Error while getting endpoint timeslots', error )
                return throwError( error )
            } )
        )
    }

    public httpCloseBooking ( queueId ) {
        let url = this.config.closeBooking
        url = url.replace( '{queueId}', queueId )

        return fromPromise( this.lbHttpService.generateHeaderForLineberty( true, false, 'validation' ) ).pipe(
            timeout( this.config.defaultHttpTimeout ),
            switchMap( ( httpOptions: any ) => {
                return this.http.put( url, null, httpOptions )
            } ),
            first(),
            retryWhen( this.lbHttpService.genericRetryStrategy( {} ) ),
            catchError( ( error ) => {
                this.errorService.logError( 'Error while getting endpoint timeslots', error )
                return throwError( error )
            } )
        )
    }

    public httpGetEndpointTimeslots ( endpointId, startAt, endAt, saveInLocalStorage = false ) {
        let url = this.config.getEndpointTimeslots
        url = url.replace( '{endpointId}', endpointId )

        return fromPromise( this.lbHttpService.generateHeaderForLineberty( false, false, 'validation' ) ).pipe(
            timeout( this.config.defaultHttpTimeout ),
            switchMap( ( httpOptions: any ) => {
                httpOptions['params']['startAt'] = startAt
                httpOptions['params']['endAt'] = endAt
                return this.http.get( url, httpOptions )
            } ),
            switchMap( ( timeslots ) => {
                console.log( 'httpGetEndpointTimeslots res' )
                console.log( timeslots )
                if ( saveInLocalStorage ) {
                    this.localStorageService.setCurrentEndpointTimeslots( timeslots )
                }
                return of( timeslots )
            } ),
            first(),
            retryWhen( this.lbHttpService.genericRetryStrategy( {} ) ),
            catchError( ( error ) => {
                this.errorService.logError( 'Error while getting endpoint timeslots', error )
                return throwError( error )
            } )
        )
    }

    public httpSetEndpointTimeslots ( timeslots, saveInLocalStorage = false ) {
        let url = this.config.setEndpointTimeslots
        url = url.replace( '{endpointId}', this.localStorageService.getSelectedEndpoint().endpointId )
        const timezone = this.getTimezone()

        const body = {
            'startAt': moment.tz( Date.now(), timezone ).startOf( 'day' ).toISOString(),
            'endAt': moment.tz( Date.now(), timezone ).endOf( 'day' ).toISOString(),
            'newTimeslots': timeslots
        }

        return fromPromise( this.lbHttpService.generateHeaderForLineberty( true, false, 'validation' ) ).pipe(
            timeout( this.config.defaultHttpTimeout ),
            switchMap( ( httpOptions: any ) => {
                return this.http.put( url, body, httpOptions )
            } ),
            switchMap( ( timeslots ) => {
                if ( saveInLocalStorage ) {
                    this.localStorageService.setCurrentEndpointTimeslots( timeslots )
                }
                return of( timeslots )
            } ),
            first(),
            retryWhen( this.lbHttpService.genericRetryStrategy( {} ) ),
            catchError( ( error ) => {
                this.errorService.logError( 'Error while setting endpoint timeslots', error )
                const errorName = error.error.error

                const data: any = {
                    width: '80vw',
                    data: {
                        text: '',
                        title: this.lbTranslationService.translateFromKey( 'endpoint_timeslotService_popupSetTimeslotsError_title', { lang: this.locale.language } ),
                        confirm: this.lbTranslationService.translateFromKey( 'endpoint_confirmDialog_confirm_ok', { lang: this.locale.language } )
                    }
                }

                switch ( errorName ) {
                    case 'INVALID_START_DATE':
                        data.data.text = this.lbTranslationService.translateFromKey( 'endpoint_timeslotService_popupSetTimeslotsError_invalidStartDate_text', { lang: this.locale.language } )
                        break
                    case 'INVALID_END_DATE':
                        data.data.text = this.lbTranslationService.translateFromKey( 'endpoint_timeslotService_popupSetTimeslotsError_invalidEndDate_text', { lang: this.locale.language } )
                        break
                    case 'INVALID_ACCOUNT':
                        data.data.text = this.lbTranslationService.translateFromKey( 'endpoint_timeslotService_popupSetTimeslotsError_invalidAccount_text', { lang: this.locale.language } )
                        break
                    case 'ENDPOINT_ID_NOT_FOUND':
                        data.data.text = this.lbTranslationService.translateFromKey( 'endpoint_timeslotService_popupSetTimeslotsError_endpointIdNotFound_text', { lang: this.locale.language } )
                        break
                    case 'FORBIDDEN_ACTION_ON_THE_ENDPOINT':
                        data.data.text = this.lbTranslationService.translateFromKey( 'endpoint_timeslotService_popupSetTimeslotsError_forbiddenActionOnTheEndpoint_text', { lang: this.locale.language } )
                        break
                    case 'FORBIDDEN_ACTION_FOR_THE_OPERATOR':
                        data.data.text = this.lbTranslationService.translateFromKey( 'endpoint_timeslotService_popupSetTimeslotsError_forbiddenActionForTheOperator_text', { lang: this.locale.language } )
                        break
                    case 'NEW_TIMESLOTS_IS_EMPTY':
                        data.data.text = this.lbTranslationService.translateFromKey( 'endpoint_timeslotService_popupSetTimeslotsError_newTimeslotsIsEmpty_text', { lang: this.locale.language } )
                        break
                    case 'NEW_TIMESLOTS_BAD_FORMAT':
                        data.data.text = this.lbTranslationService.translateFromKey( 'endpoint_timeslotService_popupSetTimeslotsError_newTimeslotsBadFormat_text', { lang: this.locale.language } )
                        break
                    case 'NEW_TIMESLOTS_INVALID_VALUE':
                        data.data.text = this.lbTranslationService.translateFromKey( 'endpoint_timeslotService_popupSetTimeslotsError_newTimeslotsInvalidValue_text', { lang: this.locale.language } )
                        break
                    case 'NEW_TIMESLOTS_START_DATE_AFTER_END_DATE':
                        data.data.text = this.lbTranslationService.translateFromKey( 'endpoint_timeslotService_popupSetTimeslotsError_newTimeslotsStartDateAfterEndDate_text', { lang: this.locale.language } )
                        break
                    case 'NEW_TIMESLOTS_OVERLAP':
                        data.data.text = this.lbTranslationService.translateFromKey( 'endpoint_timeslotService_popupSetTimeslotsError_newTimeslotsOverlap_text', { lang: this.locale.language } )
                        break
                    case 'START_DATE_NOT_TODAY':
                        data.data.text = this.lbTranslationService.translateFromKey( 'endpoint_timeslotService_popupSetTimeslotsError_startDateNotToday_text', { lang: this.locale.language } )
                        break
                    case 'END_DATE_NOT_TODAY':
                        data.data.text = this.lbTranslationService.translateFromKey( 'endpoint_timeslotService_popupSetTimeslotsError_endDateNotToday_text', { lang: this.locale.language } )
                        break
                    case 'START_DATE_AFTER_END_DATE':
                        data.data.text = this.lbTranslationService.translateFromKey( 'endpoint_timeslotService_popupSetTimeslotsError_startDateAfterEndDate_text', { lang: this.locale.language } )
                        break
                    case 'NEW_TIMESLOTS_NOT_INSIDE_PLACE_TIMESLOTS':
                        data.data.text = this.lbTranslationService.translateFromKey( 'endpoint_timeslotService_popupSetTimeslotsError_newTimeslotsNotInsidePlaceTimeslots_text', { lang: this.locale.language } )
                        break
                    case 'INTERNAL_ERROR':
                        data.data.text = this.lbTranslationService.translateFromKey( 'endpoint_timeslotService_popupSetTimeslotsError_internalError_text', { lang: this.locale.language } )
                        break
                    default:
                        data.data.text = this.lbTranslationService.translateFromKey( 'endpoint_timeslotService_popupSetTimeslotsError_unknwownError_text', { lang: this.locale.language } )
                        break
                }

                this.dialog.open( ConfirmDialogComponent, data )
                return throwError( error )
            } )
        )
    }

    httpDeleteEndpointTimeslots () {
        let url = this.config.deleteEndpointTimeslots
        url = url.replace( '{endpointId}', this.localStorageService.getSelectedEndpoint().endpointId )
        const timezone = this.getTimezone()

        return fromPromise( this.lbHttpService.generateHeaderForLineberty( true, false, 'validation' ) ).pipe(
            timeout( this.config.defaultHttpTimeout ),
            switchMap( ( httpOptions: any ) => {
                httpOptions['body'] = {}
                httpOptions['body']['startAt'] = moment.tz( Date.now(), timezone ).startOf( 'day' ).toISOString()
                httpOptions['body']['endAt'] = moment.tz( Date.now(), timezone ).endOf( 'day' ).toISOString()
                return this.http.delete( url, httpOptions )
            } ),
            first(),
            retryWhen( this.lbHttpService.genericRetryStrategy( {} ) ),
            catchError( ( error ) => {
                this.errorService.logError( 'Error while deleting endpoint timeslots', error )
                const errorName = error.error.error

                const data: any = {
                    width: '80vw',
                    data: {
                        text: '',
                        title: this.lbTranslationService.translateFromKey( 'endpoint_timeslotService_popupDeleteTimeslotsError_title', { lang: this.locale.language } ),
                        confirm: this.lbTranslationService.translateFromKey( 'endpoint_confirmDialog_confirm_ok', { lang: this.locale.language } )
                    }
                }

                switch ( errorName ) {
                    case 'INVALID_START_DATE':
                        data.data.text = this.lbTranslationService.translateFromKey( 'endpoint_timeslotService_popupSetTimeslotsError_invalidStartDate_text', { lang: this.locale.language } )
                        break
                    case 'INVALID_END_DATE':
                        data.data.text = this.lbTranslationService.translateFromKey( 'endpoint_timeslotService_popupSetTimeslotsError_invalidEndDate_text', { lang: this.locale.language } )
                        break
                    case 'INVALID_ACCOUNT':
                        data.data.text = this.lbTranslationService.translateFromKey( 'endpoint_timeslotService_popupSetTimeslotsError_invalidAccount_text', { lang: this.locale.language } )
                        break
                    case 'ENDPOINT_ID_NOT_FOUND':
                        data.data.text = this.lbTranslationService.translateFromKey( 'endpoint_timeslotService_popupSetTimeslotsError_endpointIdNotFound_text', { lang: this.locale.language } )
                        break
                    case 'FORBIDDEN_ACTION_ON_THE_ENDPOINT':
                        data.data.text = this.lbTranslationService.translateFromKey( 'endpoint_timeslotService_popupSetTimeslotsError_forbiddenActionOnTheEndpoint_text', { lang: this.locale.language } )
                        break
                    case 'FORBIDDEN_ACTION_FOR_THE_OPERATOR':
                        data.data.text = this.lbTranslationService.translateFromKey( 'endpoint_timeslotService_popupSetTimeslotsError_forbiddenActionForTheOperator_text', { lang: this.locale.language } )
                        break
                    case 'START_DATE_NOT_TODAY':
                        data.data.text = this.lbTranslationService.translateFromKey( 'endpoint_timeslotService_popupSetTimeslotsError_startDateNotToday_text', { lang: this.locale.language } )
                        break
                    case 'END_DATE_NOT_TODAY':
                        data.data.text = this.lbTranslationService.translateFromKey( 'endpoint_timeslotService_popupSetTimeslotsError_endDateNotToday_text', { lang: this.locale.language } )
                        break
                    case 'START_DATE_AFTER_END_DATE':
                        data.data.text = this.lbTranslationService.translateFromKey( 'endpoint_timeslotService_popupSetTimeslotsError_startDateAfterEndDate_text', { lang: this.locale.language } )
                        break
                    case 'INTERNAL_ERROR':
                        data.data.text = this.lbTranslationService.translateFromKey( 'endpoint_timeslotService_popupSetTimeslotsError_internalError_text', { lang: this.locale.language } )
                        break
                    default:
                        data.data.text = this.lbTranslationService.translateFromKey( 'endpoint_timeslotService_popupSetTimeslotsError_unknwownError_text', { lang: this.locale.language } )
                        break
                }

                this.dialog.open( ConfirmDialogComponent, data )
                return throwError( error )
            } )
        )
    }

    public httpGetPlaceTimeslots ( placeId, saveInLocalStorage = false ) {
        let url = this.config.getPlaceTimeslots
        url = url.replace( '{placeId}', placeId )

        return fromPromise( this.lbHttpService.generateHeaderForLineberty( false, false, 'validation' ) ).pipe(
            timeout( this.config.defaultHttpTimeout ),
            switchMap( ( httpOptions: any ) => {
                return this.http.get( url, httpOptions )
            } ),
            switchMap( ( timeslots: any ) => {
                if ( saveInLocalStorage ) {
                    this.localStorageService.setCurrentPlaceTimeslots( timeslots )
                }
                return of( timeslots )
            } ),
            first(),
            retryWhen( this.lbHttpService.genericRetryStrategy( {} ) ),
            catchError( ( error ) => {
                this.errorService.logError( 'Error while getting place timeslots', error )
                return throwError( error )
            } )
        )
    }

    public refreshPlaceAndEndpointTimeslots ( placeId, saveInLocalStorage = false ) {
        const obs: any = [
            this.httpGetPlaceTimeslots( placeId, saveInLocalStorage ),
            this.getEndpointTimeslotsOfTheDay( saveInLocalStorage )
        ]

        return forkJoin( ...obs ).pipe(
            switchMap( ( allRes: any ) => {
                console.log( 'refreshPlaceAndEndpointTimeslots' )
                console.log( allRes )
                return of( {
                    placeTimeslots: allRes[0],
                    endpointTimeslots: allRes[1]
                } )
            } )
        )
    }
}
